import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { themeBackgrounds } from '../theme/theme';

interface WelcomeBannerProps {
  onGetStarted: () => void;
}

const WelcomeBanner: React.FC<WelcomeBannerProps> = ({ onGetStarted }) => {
  const navigate = useNavigate();

  return (
    <Paper
      elevation={8}
      sx={{
        mb: 4,
        p: { xs: 3, md: 4 },
        borderRadius: 3,
        background: themeBackgrounds.default,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3,
        },
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={3}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '1.75rem', md: '2.125rem' },
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              }}
            >
              👋 Welcome to Sampark Dashboard!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.1rem',
                opacity: 0.95,
                lineHeight: 1.6,
                mb: 2,
              }}
            >
              Your comprehensive platform for building powerful WebRTC applications.
              Start by exploring features, setting up your keyset, and inviting your team.
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                size="large"
                onClick={onGetStarted}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 'bold',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.3)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                🚀 Get Started
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/documentation')}
                sx={{
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                📚 View Docs
              </Button>
            </Stack>
          </Box>

          {/* Quick Stats */}
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              minWidth: '200px',
            }}
          >
            <Stack spacing={2}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(10px)',
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.2)',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  🎯 Quick Actions
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.85rem' }}>
                  • Create your first app<br />
                  • Setup API keys<br />
                  • Invite team members
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Box>

      {/* Floating Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: 20,
          right: 30,
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.1)',
          animation: 'float 6s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-20px)' },
          },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 30,
          left: 40,
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.08)',
          animation: 'float 8s ease-in-out infinite reverse',
        }}
      />
    </Paper>
  );
};

export default WelcomeBanner;